/**
 * @file useChefStats.ts
 * @description Hook managing chef production statistics queries and timeframe state.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchChefProductionStats } from '../services/chefStats.service';
import type { ChefProductionStats, StatsTimeframe } from '../../../types/stats.types';

export const useChefStats = (venueId: string | null) => {
  const [timeframe, setTimeframe] = useState<StatsTimeframe>('day');
  const [stats, setStats] = useState<ChefProductionStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!venueId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchChefProductionStats(venueId, timeframe);
      setStats(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load production statistics');
    } finally {
      setIsLoading(false);
    }
  }, [venueId, timeframe]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    timeframe,
    setTimeframe,
    stats,
    isLoading,
    error,
    refreshStats: loadStats,
  };
};
